// Iter 83 / Phase E4 Sprint 1 — Loan Policies Master.
// Operator-only CRUD for loan policies (max principal, tenure, rate,
// foreclose mode, negative-FNF permission). D14 grants both
// `loans:admin` and `finance:approve` approval authority on individual
// loans, but ONLY `loans:admin` may CRUD policies.
import { useCallback, useEffect, useState } from "react";
import {
    Plus, PencilSimple, TrashSimple, MagnifyingGlass, Bank,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const FORECLOSE_TONE = {
    forgive_unaccrued: "bg-emerald-100 text-emerald-800",
    charge_to_date: "bg-amber-100 text-amber-800",
    charge_full: "bg-rose-100 text-rose-800",
};

const EMPTY_FORM = {
    code: "", name: "", description: "",
    max_principal: 100000, max_tenure_months: 12, default_annual_rate_pct: 12,
    min_tenure_months_employee: 0,
    foreclose_mode: "forgive_unaccrued",
    allow_negative_fnf: false,
    is_active: true,
};

export default function LoanPolicies() {
    const { can } = useAuth();
    const canEdit = can("loans:admin");
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [list, m] = await Promise.all([
                api.get("/loans/policy/list"),
                api.get("/loans/meta"),
            ]);
            setItems(list.data.items);
            setMeta(m.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load loan policies"));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const filtered = items.filter((it) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (it.code || "").toLowerCase().includes(q)
            || (it.name || "").toLowerCase().includes(q);
    });

    const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
    const openEdit = (it) => { setEditing(it); setForm(it); setOpen(true); };

    const save = async () => {
        if (!form.code?.trim() || !form.name?.trim()) {
            toast.error("Code and Name are required");
            return;
        }
        try {
            const payload = {
                ...form,
                max_principal: Number(form.max_principal),
                max_tenure_months: Number(form.max_tenure_months),
                default_annual_rate_pct: Number(form.default_annual_rate_pct),
                min_tenure_months_employee: Number(form.min_tenure_months_employee || 0),
            };
            if (editing) {
                const { code, ...patch } = payload;
                await api.patch(`/loans/policy/${editing.id}`, patch);
                toast.success(`Updated policy ${editing.code}`);
            } else {
                await api.post("/loans/policy", payload);
                toast.success(`Created policy ${payload.code}`);
            }
            setOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };
    const remove = async (it) => {
        if (!window.confirm(`Delete loan policy ${it.code}? Deactivate (is_active=false) is recommended over delete.`)) return;
        try {
            await api.delete(`/loans/policy/${it.id}`);
            toast.success(`Deleted ${it.code}`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="loan-policies-page">
            <PageHeader
                eyebrow="Loans & Advances"
                title="Loan Policies"
                description={`${items.length} polic${items.length === 1 ? "y" : "ies"} configured · governs max amount, tenure, rate & foreclose`}
                actions={canEdit ? (
                    <Button onClick={openNew} size="sm" data-testid="loan-policy-new-btn">
                        <Plus size={14} className="mr-1" /> New Policy
                    </Button>
                ) : null}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="relative max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input className="pl-9" placeholder="Search code or name…"
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        data-testid="loan-policy-search" />
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="loan-policy-table">
                {loading ? (
                    <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
                ) : (
                    <table className="w-full text-sm min-w-[1100px]">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Code</th>
                                <th className="text-left px-3 py-2 font-medium">Name</th>
                                <th className="text-right px-3 py-2 font-medium">Max Principal</th>
                                <th className="text-right px-3 py-2 font-medium">Max Tenure</th>
                                <th className="text-right px-3 py-2 font-medium">Rate p.a.</th>
                                <th className="text-right px-3 py-2 font-medium">Min Emp Tenure</th>
                                <th className="text-left px-3 py-2 font-medium">Foreclose</th>
                                <th className="text-left px-3 py-2 font-medium">Neg-FNF</th>
                                <th className="text-left px-3 py-2 font-medium">Active</th>
                                <th className="text-left px-3 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`loan-policy-row-${it.code}`}>
                                    <td className="px-3 py-2 font-mono font-semibold">{it.code}</td>
                                    <td className="px-3 py-2">{it.name}</td>
                                    <td className="px-3 py-2 text-right font-mono">₹ {Number(it.max_principal).toLocaleString("en-IN")}</td>
                                    <td className="px-3 py-2 text-right font-mono">{it.max_tenure_months} mo</td>
                                    <td className="px-3 py-2 text-right font-mono">{Number(it.default_annual_rate_pct).toFixed(2)}%</td>
                                    <td className="px-3 py-2 text-right font-mono">{it.min_tenure_months_employee || 0} mo</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-[10px] ${FORECLOSE_TONE[it.foreclose_mode] || ""}`}>
                                            {(it.foreclose_mode || "").replaceAll("_", " ")}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{it.allow_negative_fnf ? "Yes" : "No"}</td>
                                    <td className="px-3 py-2 text-xs">{it.is_active ? "Active" : <span className="text-slate-400">Inactive</span>}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1">
                                            {canEdit ? (
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`loan-policy-edit-${it.code}`}>
                                                    <PencilSimple size={12} />
                                                </Button>
                                            ) : (
                                                <span className="text-[10px] text-slate-400 italic">Read-only</span>
                                            )}
                                            {canEdit && (
                                                <Button variant="ghost" size="sm" onClick={() => remove(it)} data-testid={`loan-policy-del-${it.code}`}>
                                                    <TrashSimple size={12} className="text-rose-600" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={10} className="text-center py-8 text-slate-400 italic text-sm">No policies configured. Create one to start issuing loans.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl" data-testid="loan-policy-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bank size={18} /> {editing ? `Edit policy ${editing.code}` : "New Loan Policy"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <Label>Code *</Label>
                            <Input value={form.code} disabled={!!editing}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") })}
                                placeholder="STD_LOAN" data-testid="loan-policy-form-code" />
                        </div>
                        <div>
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="loan-policy-form-name" />
                        </div>
                        <div className="col-span-2">
                            <Label>Description</Label>
                            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div>
                            <Label>Max Principal (₹) *</Label>
                            <Input type="number" min="1" value={form.max_principal}
                                onChange={(e) => setForm({ ...form, max_principal: e.target.value })}
                                data-testid="loan-policy-form-max-principal" />
                        </div>
                        <div>
                            <Label>Max Tenure (months) *</Label>
                            <Input type="number" min="1" max="120" value={form.max_tenure_months}
                                onChange={(e) => setForm({ ...form, max_tenure_months: e.target.value })} />
                        </div>
                        <div>
                            <Label>Default Annual Rate (%)</Label>
                            <Input type="number" step="0.01" min="0" max="50" value={form.default_annual_rate_pct}
                                onChange={(e) => setForm({ ...form, default_annual_rate_pct: e.target.value })} />
                        </div>
                        <div>
                            <Label>Min Employee Tenure (months)</Label>
                            <Input type="number" min="0" value={form.min_tenure_months_employee}
                                onChange={(e) => setForm({ ...form, min_tenure_months_employee: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <Label>Foreclose Mode (D15)</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={form.foreclose_mode}
                                onChange={(e) => setForm({ ...form, foreclose_mode: e.target.value })}
                                data-testid="loan-policy-form-foreclose">
                                {(meta?.foreclose_modes || []).map((m) => (
                                    <option key={m} value={m}>{m.replaceAll("_", " ")}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-slate-500 mt-1">
                                <span className="font-semibold">forgive_unaccrued</span>: pay outstanding only · <span className="font-semibold">charge_to_date</span>: + 1 month interest on balance · <span className="font-semibold">charge_full</span>: + all unaccrued interest
                            </p>
                        </div>
                        <div className="col-span-2 flex gap-4 mt-1">
                            <label className="flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={form.allow_negative_fnf}
                                    onChange={(e) => setForm({ ...form, allow_negative_fnf: e.target.checked })}
                                    data-testid="loan-policy-form-negfnf" />
                                Allow Negative FNF on exit (D16)
                            </label>
                            <label className="flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                Active
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="loan-policy-save">{editing ? "Save changes" : "Create policy"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
